import React, { useEffect, useRef, useState, useCallback } from "react"
import "./ProrgamList.scss"
import programApi from "Api/programApi"
import {
  Button,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from 'primereact/toast'
import { OverlayPanel } from "primereact/overlaypanel"
import LogsApi from "Api/LogsApi"
import LogsSidebar from "components/LogsSidebar/LogsSidebar"

const ProgramList = () => {
  document.title = "Program"
  const uploadbtn = useRef()
  const navigate = useNavigate("")
  const accessToken = localStorage.getItem("accessToken")
  const [programDetails, setProgramDetails] = useState([])
  const [institutions, setInstitutions] = useState([])
  const [courses, setCourses] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const userData = JSON.parse(localStorage.getItem("userData"))
  const [loading, setLoading] = useState(true)
  const [addProgramLoader, setAddProgramLoader] = useState(false)
  const [filterStatus, setFilterStatus] = useState("all")
  const [filteredProgramDetails, setFilteredProgramDetails] = useState([])
  const toast = useRef(null)
  const [selectedPrograms, setSelectedPrograms] = useState([])
  const os = useRef(null)
  const [logsVisible, setLogsVisible] = useState(false)
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [allLogs, setAllLogs] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const searchTimeoutRef = useRef(null)

  const getAllProgramDetails = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    programApi
      .getProgram(config)
      .then(response => {
        if (response.data.status) {
          setLoading(false)
          setProgramDetails(response.data.program)
          setTotalRecords(response.data.totalRecords)
        }
      })
      .catch(error => {
        console.log("Error Fetching Program", error)
      })
  }

  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!query || query.trim() === "") {
        getAllProgramDetails()
        setInstitutions([])
        setCourses([])
        return
      }

      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          query: query,
          limit: limit,
          page: page,
        },
      }

      try {
        const response = await programApi.searchCourseAndProgram(config)
        if (response.data.status) {
          setProgramDetails(response.data.data.programs || [])
          setInstitutions(response.data.data.institutions || [])
          setCourses(response.data.data.courses || [])
          setFilteredProgramDetails(response.data.data.programs || [])
          setTotalRecords(response.data.data.programs?.length || 0)
        } else {
          setProgramDetails([])
          setInstitutions([])
          setCourses([])
          setFilteredProgramDetails([])
          setTotalRecords(0)
          toast.current.show({
            severity: "info",
            summary: "No Results",
            detail: response.data.message || "No results found matching your search",
            life: 3000,
          })
        }
      } catch (error) {
        setProgramDetails([])
        setInstitutions([])
        setCourses([])
        setFilteredProgramDetails([])
        setTotalRecords(0)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to search",
          life: 3000,
        })
      }
    }, 800)
  }, [accessToken, page, limit])

  const handleSearch = e => {
    const value = e.target.value
    setSearchQuery(value)
    setPage(1) // Reset to first page when searching
    debouncedSearch(value)
  }

  // Update useEffect to handle search pagination
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      debouncedSearch(searchQuery)
    } else {
      getAllProgramDetails()
    }
  }, [page, limit, searchQuery])

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    let filteredData = programDetails

    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        program => program.verifiedStatus === filterStatus
      )
    }

    setFilteredProgramDetails(filteredData)
  }, [filterStatus, programDetails])

  const totalPages = Math.ceil(totalRecords / limit)

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Program", link: "#" },
    { label: "Program List", link: "#" },
  ]

  const handleUploadButtonClick = () => {
    uploadbtn.current.click()
  }

  const [excelLoader, setExcelLoader] = useState(false)

  const handleFileChange = event => {
    setExcelLoader(true)

    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    programApi
      .uploadProgramByExcel(formData, config)
      .then(res => {
        getAllProgramDetails()
        console.log(res.data)
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message, life: 3000 });
      })
      .catch(err => {
        console.log(err)
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({ severity: 'error', summary: 'Error', detail: err.response.data.message, life: 3000 });
      })
  }
  const handleAddProgramClick = () => {
    setAddProgramLoader(true)
    setTimeout(() => {
      setAddProgramLoader(false)
      navigate("/addProgram")
    }, 500)
  }

  const toggleSelectAll = () => {
    if (selectedPrograms.length === filteredProgramDetails.length) {
      setSelectedPrograms([])
    } else {
      setSelectedPrograms(filteredProgramDetails.map(program => program._id))
    }
  }

  const fetchLogs = async () => {
    setLogsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
      const response = await LogsApi.getLogsByServiceName("program-service", config)
      const sortedLogs = response.data.logs.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      )
      setAllLogs(sortedLogs)
      setLogs(sortedLogs)
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch logs",
        life: 3000,
      })
    } finally {
      setLogsLoading(false)
    }
  }

  const handleLogsClick = () => {
    setLogsVisible(true)
    fetchLogs()
  }

  const handleFilterChange = (level) => {
    if (level === 'all') {
      setLogs(allLogs);
    } else {
      setLogs(allLogs.filter(log => log.eventType.toLowerCase() === level));
    }
  };

  const toggleSelection = programId => {
    setSelectedPrograms(prevPrograms =>
      prevPrograms.includes(programId)
        ? prevPrograms.filter(id => id !== programId)
        : [...prevPrograms, programId],
    )
  }

  const handleVerifyProgram = (status) => {
    if (selectedPrograms.length === 0) {
      toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please select at least one Program to verify.', life: 3000 });
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const payload = {
      programIds: selectedPrograms,
      newStatus: status
    }

    programApi.updateProgramStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({ severity: 'success', summary: 'Success', detail: response.data.message, life: 3000 });
          getAllProgramDetails()
          setSelectedPrograms([])
        }
      })
      .catch(error => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to update Program status.', life: 3000 });
      })
  }

  return (
    <>
      <LogsSidebar
        visible={logsVisible}
        onHide={() => setLogsVisible(false)}
        logs={logs}
        loading={logsLoading}
        onRefresh={fetchLogs}
        onFilterChange={handleFilterChange}
        allLogs={allLogs}
      />
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="university-list-header d-flex  align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Program" />

            <div className="d-flex align-items-center gap-3">
              {selectedPrograms.length === 0 && (
                <>
                  <Input
                    type="text"
                    placeholder="Search Programs, Courses, or Institutions"
                    className="form-control"
                    style={{ width: "300px" }}
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <input
                    type="file"
                    ref={uploadbtn}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    color="success"
                    className="waves-effect"
                    onClick={handleUploadButtonClick}
                  >
                    {excelLoader ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-microsoft-excel" />
                    )}
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect"
                    onClick={handleAddProgramClick}
                  >
                    Add Program
                  </Button>

                  <Button
                    type="button"
                    color="info"
                    className="waves-effect"
                    style={{ minWidth: "100px" }}
                    onClick={handleLogsClick}
                  >
                    <i className="mdi mdi-file-document-outline me-1" />
                    Logs
                  </Button>
                </>
              )}

              {selectedPrograms.length > 0 && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    onClick={() => handleVerifyProgram("approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    className="waves-effect"
                    onClick={() => handleVerifyProgram("reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>

          {searchQuery && (institutions.length > 0 || courses.length > 0) && (
            <div className="search-results mt-3">
              {institutions.length > 0 && (
                <div className="mb-3">
                  <h5>Institutions ({institutions.length})</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {institutions.map(institution => (
                      <div key={institution._id} className="search-item p-2 border rounded">
                        <div className="fw-bold">{institution.institutionName}</div>
                        <div className="small text-muted">{institution.institutionCode}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {courses.length > 0 && (
                <div className="mb-3">
                  <h5>Courses ({courses.length})</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {courses.map(course => (
                      <div key={course._id} className="search-item p-2 border rounded">
                        <div className="fw-bold">{course.courseName}</div>
                        <div className="small text-muted">{course.duration}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="prorgam-data-table table-responsive mt-4 mb-3 ">
            {loading ? (
              <DataTableLoader cols={10} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onClick={toggleSelectAll}
                        checked={
                          selectedPrograms.length ===
                          filteredProgramDetails.length
                        }
                      />
                    </th>
                    <th>#</th>
                    <th>Program Name</th>
                    <th>University</th>
                    <th>Institution</th>
                    <th>
                      <span className="d-flex align-items-center gap-2 justify-content-center">
                        <span>Verified Status</span>
                        <span
                          onClick={e => os.current.toggle(e)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="mdi mdi-filter" />
                        </span>
                      </span>
                      <OverlayPanel ref={os}>
                        <Input
                          type="select"
                          value={filterStatus}
                          onChange={e => {
                            setFilterStatus(e.target.value)
                            os.current.hide()
                          }}
                          style={{ width: "150px" }}
                        >
                          <option value="all">All</option>
                          <option value="approve">Approved</option>
                          <option value="reject">Rejected</option>
                          <option value="pending">Pending</option>
                        </Input>
                      </OverlayPanel>
                    </th>
                    <th>Edit</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProgramDetails.map((program, index) => (
                    <tr key={program._id} className="university-table-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPrograms.includes(program._id)}
                          onClick={() => {
                            toggleSelection(program._id)
                          }}
                        />
                      </td>
                      <th scope="row">{(page - 1) * limit + index + 1}</th>
                      <td>{program.programName}</td>
                      <td>{program.university?.universityName}</td>
                      <td>{program.institution?.institutionName}</td>
                      <td>
                        <span
                          className={`status ${program.verifiedStatus?.toLowerCase() || 'pending'}`}
                        >
                          {program.verifiedStatus === "approve"
                            ? "Approved"
                            : program.verifiedStatus === "reject"
                              ? "Rejected"
                              : "Pending"}
                        </span>
                      </td>

                      <td>
                        <span
                          className="edit-prorgam"
                          onClick={() => {
                            navigate(`/edit-program/${program?._id}`, {
                              state: program,
                            })
                          }}
                        >
                          <i className="ion ion-md-create" />
                        </span>
                      </td>
                      <td>
                        <span
                          onClick={() => {
                            navigate(`/view-program/${program?._id}`)
                          }}
                          className="view-program"
                        >
                          <i className="ion ion-md-eye" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
          <div className="pagination-container d-flex align-items-center justify-content-end">
            <Pagination>
              <PaginationItem disabled={page <= 1}>
                <PaginationLink previous onClick={() => setPage(page - 1)} />
              </PaginationItem>
              {[...Array(Math.min(3, totalPages))].map((_, index) => (
                <PaginationItem key={index} active={index + 1 === page}>
                  <PaginationLink onClick={() => setPage(index + 1)}>
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem active>
                  <PaginationLink onClick={() => setPage(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 3 &&
                [...Array(Math.min(3, totalPages))].map((_, index) => {
                  const pageNumber = totalPages - 2 + index
                  return (
                    <PaginationItem
                      key={pageNumber}
                      active={pageNumber === page}
                    >
                      <PaginationLink onClick={() => setPage(pageNumber)}>
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}
              <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => setPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </Col>
      </Row>
    </>
  )
}

export default ProgramList
